import { Theme } from '@logto/schemas';
import { useEffect, useRef } from 'react';

import hytchLogoDark from '@/shared/assets/hytch/hytch-logo-dark.png';
import hytchLogoLight from '@/shared/assets/hytch/hytch-logo-light.jpeg';

import styles from './index.module.scss';

const guardStyleSelector = 'style[data-logto-signature-guard="true"]';

const signatureGuardStyle = `
[data-logto-signature-container="secured"][data-logto-signature-container="secured"] {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

[data-logto-signature="secured"][data-logto-signature="secured"] {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  font: var(--font-label-2) !important;
  font-weight: normal !important;
  color: var(--color-neutral-variant-60) !important;
  padding: 4px 8px !important;
  text-decoration: none !important;
  opacity: 75% !important;
  direction: ltr !important;
  position: relative !important;
  inset: auto !important;
  left: auto !important;
  right: auto !important;
  top: auto !important;
  bottom: auto !important;
  transform: none !important;
  pointer-events: auto !important;
}

[data-logto-signature="secured"][data-logto-signature="secured"]:is(:hover, :active, :focus-visible) {
  opacity: 100% !important;
}

[data-logto-signature="secured"][data-logto-signature="secured"] [data-logto-signature-icon="static"] {
  display: block !important;
}

[data-logto-signature="secured"][data-logto-signature="secured"] [data-logto-signature-icon="highlight"] {
  display: none !important;
}

[data-logto-signature="secured"][data-logto-signature="secured"]:is(:hover, :active, :focus-visible)
  [data-logto-signature-icon="static"] {
  display: none !important;
}

[data-logto-signature="secured"][data-logto-signature="secured"]:is(:hover, :active, :focus-visible)
  [data-logto-signature-icon="highlight"] {
  display: block !important;
}

[data-logto-signature-text] {
  margin-inline-end: 4px !important;
}

body.mobile [data-logto-signature="secured"][data-logto-signature="secured"] {
  color: var(--color-neutral-variant-80) !important;
  font: var(--font-label-3) !important;
}
`;

type Props = {
  readonly className?: string;
  readonly theme: Theme;
};

const LogtoSignature = ({ className, theme }: Props) => {
  const hytchLogo = theme === Theme.Light ? hytchLogoLight : hytchLogoDark;

  const containerRef = useRef<HTMLDivElement>(null);
  const signatureRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const { current: container } = containerRef;
    const { current: signature } = signatureRef;

    if (!signature) {
      return;
    }

    const ensureGuardStyle = (): { created: boolean; element: HTMLStyleElement } => {
      const existing = document.head.querySelector<HTMLStyleElement>(guardStyleSelector);

      if (existing) {
        return { created: false, element: existing };
      }

      const createdElement = document.createElement('style');
      Reflect.set(createdElement.dataset, 'logtoSignatureGuard', 'true');
      createdElement.append(signatureGuardStyle);
      document.head.append(createdElement);

      return { created: true, element: createdElement };
    };

    const { created, element: guardStyleElement } = ensureGuardStyle();

    const enforceIntegrity = () => {
      if (container) {
        container.removeAttribute('hidden');
        container.style.setProperty('display', 'block', 'important');
        container.style.setProperty('visibility', 'visible', 'important');
        container.style.setProperty('opacity', '1', 'important');
      }

      signature.removeAttribute('hidden');

      if (styles.signature && !signature.classList.contains(styles.signature)) {
        signature.classList.add(styles.signature);
      }

      signature.style.removeProperty('display');
      signature.style.removeProperty('visibility');
      signature.style.removeProperty('opacity');
      signature.style.removeProperty('position');
      signature.style.removeProperty('left');
      signature.style.removeProperty('right');
      signature.style.removeProperty('top');
      signature.style.removeProperty('bottom');
      signature.style.removeProperty('transform');
    };

    enforceIntegrity();

    const observer = new MutationObserver(() => {
      enforceIntegrity();
    });

    observer.observe(signature, {
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });

    if (container) {
      observer.observe(container, {
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden'],
      });
    }

    const intervalId = window.setInterval(enforceIntegrity, 2000);

    return () => {
      observer.disconnect();
      window.clearInterval(intervalId);

      if (created) {
        guardStyleElement.remove();
      }
    };
  }, []);

  return (
    <div ref={containerRef} className={className} data-logto-signature-container="secured">
      <span
        ref={signatureRef}
        aria-label="Powered by Hytch Auth"
        className={styles.signature}
        data-logto-signature="secured"
      >
        <span data-logto-signature-text className={styles.text}>
          Powered by
        </span>
        <img
          data-logto-signature-icon="static"
          className={styles.staticIcon}
          src={hytchLogo}
          alt="Hytch Auth"
        />
        <img
          data-logto-signature-icon="highlight"
          className={styles.highlightIcon}
          src={hytchLogo}
          alt="Hytch Auth"
        />
      </span>
    </div>
  );
};

export default LogtoSignature;
